import {
  VictoryAxis, VictoryLine, VictoryChart,
  VictoryScatter,
  VictoryArea,
  VictoryVoronoiContainer,
  VictoryTheme,
  VictoryGroup,
  VictoryTooltip,
} from 'victory';
import { CategoricalData } from '../../api-models/types.gen';
import tailwindConfig from '../../../tailwind.config';
import { AreaGradient } from '../../shared/components';

const valueFormatter = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

interface LineGraphProps {
  graphData: CategoricalData[];
  showAxis: boolean;
  colour?: string;
  gradient: boolean;
  height?: number;
  width?: number;
  responsive?: boolean;
  showGridlines?: boolean;
}

export default function LineGraph({
  graphData, showAxis, gradient, colour, height, width,
  responsive = true, showGridlines = false,
}: LineGraphProps) {
  const graphColour = colour || tailwindConfig.theme.extend.colors.pipblue.DEFAULT;
  const defaultLabelStyle = { fontSize: 14, fontFamily: 'Nunito, sans-serif' };
  const borderStyle = { stroke: '#d1d5db', strokeDasharray: '4,4' };
  const noBorderStyle = { stroke: 'transparent' };

  const sharedAxisStyle = {
    axis: showAxis ? borderStyle : noBorderStyle,
    tickLabels: { ...defaultLabelStyle, fill: showAxis ? '#000' : 'transparent' },
  };

  const xAxisStyle = {
    ...sharedAxisStyle,
    grid: showGridlines ? borderStyle : noBorderStyle,
  };

  const yAxisStyle = {
    ...sharedAxisStyle,
    ticks: { stroke: ({ tick }: { tick?: number }) => (showAxis && Number.isInteger(tick) ? '#d1d5db' : 'transparent') },
    grid: {
      ...(showGridlines ? borderStyle : noBorderStyle),
      stroke: ({ tick }: { tick?: number }) => (showGridlines && Number.isInteger(tick) ? '#d1d5db' : 'transparent'),
    },
  };

  if (graphData.length <= 1) {
    return <div className="text-sm text-center">Graph unavailable</div>;
  }

  const mappedData = graphData.map((datum) => ({ x: datum.group, y: datum.value }));

  // Override y-min if !showAxis (aka sparklines) to avoid the graph looking flat when the values are all high
  const yValues = mappedData.map((d) => d.y).filter((v): v is number => v !== null);
  const yMin = yValues.length > 0 ? Math.min(...yValues) : 0;
  const yMax = yValues.length > 0 ? Math.max(...yValues) : 0;
  const yDomain: [number, number] | undefined = !showAxis && yMin > 0 ? [yMin * 0.9, yMax * 1.1] : undefined;

  return (
    <div className="w-full h-full">
      <VictoryChart
        height={height}
        width={width}
        domain={yDomain ? { y: yDomain } : undefined}
        domainPadding={{ x: 0, y: 20 }}
        padding={{
          top: 0,
          bottom: showAxis ? 30 : 0,
          left: showAxis ? 45 : 0,
          right: showAxis ? 45 : 0,
        }}
        containerComponent={<VictoryVoronoiContainer responsive={responsive} preserveAspectRatio="none" />}
        theme={VictoryTheme.clean}
      >
        <VictoryAxis style={xAxisStyle} fixLabelOverlap />

        <VictoryAxis
          dependentAxis
          crossAxis={false}
          style={yAxisStyle}
          tickFormat={(tick: number) => (Number.isInteger(tick) ? valueFormatter.format(tick) : '')}
        />
        {gradient && (
          <AreaGradient id="areaGradient" colour={graphColour} startOpacity={0.5} endOpacity={0.005} />
        )}
        {gradient && (
          <VictoryArea
            style={{ data: { fill: 'url(#areaGradient)' } }}
            data={mappedData}
          />
        )}
        <VictoryGroup
          data={mappedData}
          labels={({ datum }: { datum: { y: number } }) => valueFormatter.format(datum.y)}
          labelComponent={(
            <VictoryTooltip
              style={defaultLabelStyle}
              flyoutStyle={{ stroke: '#737373' }}
            />
          )}
        >
          <VictoryLine
            style={{ data: { stroke: graphColour } }}
          />
          <VictoryScatter
            size={({ active }) => (active ? 6 : 4)}
            style={{ data: { fill: graphColour } }}
          />
        </VictoryGroup>
      </VictoryChart>
    </div>
  );
}
